import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { auth, db, functions } from './firebase';
import { doc, getDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import ActivityMap from './ActivityMap';
import { useWaterBasedStyles } from './hooks/useWaterBasedStyles';
import './App.css';
import './HistoryPage.css';
import './SharePage.css';
import './LandingPage.css';
import './TargettedActivityGeneration.css';
import { usePostHog } from 'posthog-js/react';

function TargettedActivityGeneration({ user }) {
    const { stravaId } = useParams(); // Get Strava ID from URL
    const navigate = useNavigate();
    const posthog = usePostHog();

    // State for the pre-selected activity
    const [targetActivity, setTargetActivity] = useState(null);
    const [isLoadingActivity, setIsLoadingActivity] = useState(true);
    const [activityError, setActivityError] = useState(null);

    // State for templates and generation
    const [promptTemplates, setPromptTemplates] = useState([]);
    const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [statusError, setStatusError] = useState('');

    // State for Strava connection
    const [stravaConnected, setStravaConnected] = useState(false);

    // Pagination state for map styles
    const [currentStylePage, setCurrentStylePage] = useState(1);
    const itemsPerPage = 9;

    // State for checking previous generations
    const [isCheckingPreviousGenerations, setIsCheckingPreviousGenerations] = useState(true);
    const [previousGenerations, setPreviousGenerations] = useState([]);

    // Section visibility
    const [isStylesSectionExpanded, setIsStylesSectionExpanded] = useState(true);

    // Water analysis state
    const [waterAnalysis, setWaterAnalysis] = useState(null);
    const [waterThreshold, setWaterThreshold] = useState(5.0);
    const [isAdmin, setIsAdmin] = useState(false);

    // Use the water-based styles hook for filtering
    const {
        defaultStyles: filteredTemplates,
        recommendedStyles,
        waterSummary,
        isWaterAnalyzed
    } = useWaterBasedStyles(promptTemplates, waterAnalysis, waterThreshold);

    // Load water threshold from settings and check admin status
    useEffect(() => {
        const loadWaterThreshold = async () => {
            try {
                const settingsDocRef = doc(db, 'settings', 'mapGeneration');
                const settingsDoc = await getDoc(settingsDocRef);
                if (settingsDoc.exists()) {
                    const settingsData = settingsDoc.data();
                    if (settingsData?.waterThreshold && typeof settingsData.waterThreshold === 'number') {
                        setWaterThreshold(settingsData.waterThreshold);
                    }
                }
            } catch (error) {
                console.error('Error loading water threshold:', error);
                // Keep default value of 5.0
            }
        };

        const checkAdminStatus = async () => {
            if (user) {
                try {
                    const idTokenResult = await user.getIdTokenResult();
                    setIsAdmin(!!idTokenResult.claims.admin);
                } catch (error) {
                    console.error('Error checking admin status:', error);
                    setIsAdmin(false);
                }
            } else {
                setIsAdmin(false);
            }
        };

        loadWaterThreshold();
        checkAdminStatus();
    }, [user]);

    // Handler for water analysis results from ActivityMap
    const handleWaterAnalysis = (analysis) => {
        setWaterAnalysis(analysis);
        if (localStorage.getItem('waterDetectionDebug') === 'true') {
            console.log('TargettedActivityGeneration: Water analysis received:', analysis);
        }
    };

    // Computed values for map styles pagination with default sort by date (newest first)
    const sortedStyles = [...filteredTemplates].sort((a, b) => {
        const dateA = a.createdAt?.toDate() || new Date(0);
        const dateB = b.createdAt?.toDate() || new Date(0);
        return dateB - dateA; // Newest first
    });

    const styleStartIndex = (currentStylePage - 1) * itemsPerPage;
    const styleEndIndex = styleStartIndex + itemsPerPage;
    const currentStyles = sortedStyles.slice(styleStartIndex, styleEndIndex);
    const totalStylePages = Math.ceil(sortedStyles.length / itemsPerPage);

    // Check for previous generations and redirect if any exist
    const checkPreviousGenerations = useCallback(async () => {
        if (!stravaId) return false;

        console.log(`🔍 Checking for previous generations for activity ID: ${stravaId}`);
        setIsCheckingPreviousGenerations(true);
        try {
            const getPreviousGenerationsFunc = httpsCallable(functions, 'getPreviousGenerationsByStravaId');
            const result = await getPreviousGenerationsFunc({ activityId: stravaId });

            console.log('📊 Previous generations check result:', result.data);

            if (result.data && Array.isArray(result.data.generations) && result.data.generations.length > 0) {
                console.log(`✅ Found ${result.data.generations.length} previous generation(s) for activity ${stravaId}`);
                console.log('📋 Previous generations:', result.data.generations.map(gen => ({
                    id: gen.id,
                    createdAt: gen.createdAt,
                    status: gen.status
                })));

                // Sort by creation date to get the most recent
                const sortedGenerations = result.data.generations.sort((a, b) => {
                    const dateA = new Date(a.createdAt);
                    const dateB = new Date(b.createdAt);
                    return dateB - dateA; // Most recent first
                });

                // Redirect to the most recent generation
                const mostRecentGeneration = sortedGenerations[0];
                console.log(`🚀 Redirecting to most recent generation: ${mostRecentGeneration.id} (created: ${mostRecentGeneration.createdAt})`);
                navigate(`/map/${mostRecentGeneration.id}`);
                return true; // Indicate that a redirect occurred
            }

            // No previous generations found, continue with normal flow
            console.log(`❌ No previous generations found for activity ${stravaId}. Continuing with normal flow.`);
            setPreviousGenerations(result.data?.generations || []);
            return false; // No redirect occurred
        } catch (error) {
            console.error("❗ Error checking previous generations:", error);
            console.log(`⚠️ Failed to check previous generations for activity ${stravaId}. Continuing with normal flow.`);
            // Continue with normal flow even if check fails
            setPreviousGenerations([]);
            return false; // No redirect occurred
        } finally {
            setIsCheckingPreviousGenerations(false);
        }
    }, [stravaId, navigate]);

    // Fetch prompt templates
    const fetchPromptTemplates = useCallback(async () => {
        if (!user) return;
        setIsLoadingTemplates(true);
        try {
            const getTemplatesFunc = httpsCallable(functions, 'getPromptTemplates');
            const result = await getTemplatesFunc();
            if (result.data && Array.isArray(result.data.templates)) {
                setPromptTemplates(result.data.templates);
            } else {
                setPromptTemplates([]);
            }
        } catch (error) {
            console.error("Error fetching prompt templates:", error);
            setPromptTemplates([]);
        } finally {
            setIsLoadingTemplates(false);
        }
    }, [user]);

    // Check Strava connection
    const checkStravaConnection = useCallback(async () => {
        if (!user) return false;
        try {
            const tokenDocRef = doc(db, 'users', user.uid, 'stravaTokens', 'tokens');
            const docSnap = await getDoc(tokenDocRef);
            const connected = docSnap.exists();
            setStravaConnected(connected);
            return connected;
        } catch (error) {
            console.error("Error checking Strava connection:", error);
            setStravaConnected(false);
            return false;
        }
    }, [user]);

    // Fetch specific activity by Strava ID
    const fetchTargetActivity = useCallback(async () => {
        if (!user || !stravaId) return;

        setIsLoadingActivity(true);
        setActivityError(null);

        try {
            // First check if Strava is connected
            const isConnected = await checkStravaConnection();
            if (!isConnected) {
                setActivityError("Please connect your Strava account to view this activity.");
                setIsLoadingActivity(false);
                return;
            }

            // Fetch the specific activity by ID using the new Cloud Function
            const getActivityByIdFunc = httpsCallable(functions, 'getStravaActivityById');
            const result = await getActivityByIdFunc({ activityId: stravaId });

            if (result.data) {
                setTargetActivity(result.data);
            } else {
                console.log(`🚫 Activity ${stravaId} not found or not accessible, redirecting to landing page`);
                navigate('/');
                return;
            }
        } catch (error) {
            console.error("Error fetching target activity:", error);

            // Check if this is an "Activity not found" or access error
            const errorMessage = error.message || '';
            if (errorMessage.includes('Activity not found') || 
                errorMessage.includes('not have access') || 
                errorMessage.includes('private')) {
                console.log(`🚫 Activity ${stravaId} not accessible (${errorMessage}), redirecting to landing page`);
                navigate('/');
                return;
            }

            // For other errors, show error message
            if (error.message) {
                setActivityError(error.message);
            } else {
                setActivityError("Failed to load activity. Please try again.");
            }
        } finally {
            setIsLoadingActivity(false);
        }
    }, [user, stravaId, checkStravaConnection, navigate]);

    // Initialize data on component mount
    useEffect(() => {
        // Reset water analysis when component mounts or activity changes
        setWaterAnalysis(null);
        
        // First check for previous generations - if found, will redirect regardless of auth status
        checkPreviousGenerations().then((didRedirect) => {
            // Only proceed with auth checks and data fetching if we didn't redirect
            if (didRedirect) {
                console.log('🔄 Redirect occurred, stopping further execution');
                return;
            }

            if (!user) {
                console.log('👤 No user logged in, redirecting to landing page');
                navigate('/'); // Redirect to home if not logged in
                return;
            }

            // Only fetch activity and templates if we didn't redirect and user is logged in
            console.log('📱 User logged in, fetching activity and templates');
            fetchTargetActivity();
            fetchPromptTemplates();
        });
    }, [checkPreviousGenerations, user, fetchTargetActivity, fetchPromptTemplates, navigate]);

    // Helper functions
    const formatDuration = (totalSeconds) => {
        if (isNaN(totalSeconds) || totalSeconds < 0) return 'N/A';
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = Math.floor(totalSeconds % 60);

        const pad = (num) => String(num).padStart(2, '0');

        if (hours > 0) {
            return `${hours}:${pad(minutes)}:${pad(seconds)}`;
        }
        return `${pad(minutes)}:${pad(seconds)}`;
    };

    const handleTemplateSelect = (template) => {
        posthog?.capture('prompt_template_selected', {
            template_id: template.id,
            template_name: template.name,
            page: 'targeted_generation',
            activity_id: stravaId
        });
        setSelectedTemplate(template);
        setIsStylesSectionExpanded(false);
        setTimeout(() => {
            document.getElementById('build-myth-section')?.scrollIntoView({ behavior: 'smooth' });
        }, 300);
    };

    const toggleStylesSection = () => {
        setIsStylesSectionExpanded(!isStylesSectionExpanded);
    };

    const handleGenerateMap = async () => {
        posthog?.capture('map_generation_started', {
            activity_id: stravaId,
            style: selectedTemplate?.name || selectedTemplate?.id,
            style_id: selectedTemplate?.id,
            user_id: user?.uid,
            generation_type: 'targeted',
            timestamp: new Date().toISOString()
        });

        if (!user || !targetActivity || !selectedTemplate) return;

        setIsGenerating(true);
        setStatusError('');

        try {
            // Use the proper server-side function that includes limit checking
            const requestImageGenerationFunc = httpsCallable(functions, 'requestImageGeneration');
            
            const requestPayload = {
                StravaId: targetActivity.id.toString(),
                promptTemplateId: selectedTemplate.id,
                gpxDataSource: 'polyline',
                // Include Strava stats for the generation pipeline
                stravaStats: {
                    activityName: targetActivity.name,
                    activityType: targetActivity.type,
                    distance: targetActivity.distance,
                    movingTime: targetActivity.moving_time,
                    elevationGain: targetActivity.total_elevation_gain,
                    averageSpeed: targetActivity.average_speed,
                    averageWatts: targetActivity.average_watts !== undefined ? targetActivity.average_watts : null,
                    activityDate: { seconds: new Date(targetActivity.start_date).getTime() / 1000 }
                }
            };

            const result = await requestImageGenerationFunc(requestPayload);
            console.log("Server response:", result.data);
            
            // Navigate to the map page with the request ID
            navigate(`/map/${result.data.requestId}`);
            
        } catch (e) {
            console.error("Error submitting request:", e);
            
            // Handle specific error types
            if (e.code === 'functions/resource-exhausted') {
                setStatusError("You have run out of credits for this month.");
                posthog?.capture('out_of_credits', {
                    context: 'targeted_generation_error',
                    error_code: e.code,
                    activity_id: stravaId,
                    timestamp: new Date().toISOString()
                });
            } else if (e.code === 'functions/already-exists') {
                // Handle concurrent generation error
                const details = e.details;
                if (details && details.existingRequestId) {
                    setStatusError(
                        <span>
                            You already have a map being generated. 
                            <a href={`/map/${details.existingRequestId}`} style={{ marginLeft: '5px', color: '#007bff', textDecoration: 'underline' }}>
                                View in-progress generation
                            </a>
                        </span>
                    );
                } else {
                    setStatusError("You already have a map being generated. Please wait for it to complete before starting a new one.");
                }
                posthog?.capture('concurrent_generation_blocked', {
                    context: 'targeted_generation',
                    activity_id: stravaId,
                    timestamp: new Date().toISOString()
                });
            } else {
                setStatusError(`Error: ${e.message}`);
            }
            setIsGenerating(false);
        }
    };

    return (
        <div className="container targetted-generation-page">
            {/* Show loading while checking for previous generations */}
            {isCheckingPreviousGenerations ? (
                <div style={{ textAlign: 'center', padding: '100px 20px' }}>
                    <h2>Checking for existing maps...</h2>
                    <p>Looking for previously generated maps for this activity.</p>
                </div>
            ) : !user ? (
                <div style={{ textAlign: 'center', padding: '100px 20px' }}>
                    <h2>Redirecting...</h2>
                    <p>Please log in to continue.</p>
                </div>
            ) : (
                <>
                    {/* Header */}
                    <div className="share-header-row" style={{ marginBottom: '30px' }}>
                        <div>
                            <h1 style={{
                                fontSize: '30px',
                                fontWeight: '500',
                                lineHeight: '1.27',
                                color: 'var(--Gray-900, #101828)',
                                margin: '0 0 4px 0'
                            }}>Create Your Mythic Map</h1>
                            <p className="history-subtitle">Transform your Strava activity into a legendary adventure</p>
                        </div>
                    </div>

                    <div className="history-divider"></div>

                    {/* Step 1: Pre-selected Activity */}
                    <div className="activity-section" style={{ marginBottom: '40px' }}>
                        <h2 style={{
                            fontSize: '30px',
                            fontWeight: '500',
                            lineHeight: '1.27',
                            color: 'var(--Gray-900, #101828)',
                            margin: '0 0 4px 0'
                        }}>1. Your Selected Activity</h2>
                        <p className="section-subtitle">This activity was selected from your Strava link</p>
                        <div className="history-divider"></div>

                        {isLoadingActivity ? (
                            <div style={{ textAlign: 'center', padding: '40px' }}>
                                <p>Loading your activity...</p>
                            </div>
                        ) : activityError ? (
                            <div className="share-error" style={{ textAlign: 'center', padding: '40px' }}>
                                <h3>Unable to Load Activity</h3>
                                <p>{activityError}</p>
                                {!stravaConnected && (
                                    <p>Please connect your Strava account in the navigation bar above.</p>
                                )}
                            </div>
                        ) : targetActivity ? (
                            <div className="activity-card">
                                <div className="activity-thumbnail">
                                    <div className="activity-map-container">
                                        <ActivityMap
                                            activity={targetActivity}
                                            width={200}
                                            height={144}
                                            onWaterAnalysis={handleWaterAnalysis}
                                        />
                                    </div>
                                </div>

                                <div className="activity-details">
                                    <div className="activity-title">
                                        <h3>{targetActivity.name}</h3>
                                    </div>

                                    <div className="activity-stats">
                                        <div className="stat">
                                            <span className="stat-label">Distance</span>
                                            <span className="stat-value">
                                                {(targetActivity.distance / 1000).toFixed(1)} km
                                            </span>
                                        </div>
                                        <div className="stat">
                                            <span className="stat-label">Elevation gain</span>
                                            <span className="stat-value">
                                                {targetActivity.total_elevation_gain?.toFixed(0) || 'N/A'} m
                                            </span>
                                        </div>
                                        <div className="stat">
                                            <span className="stat-label">Time</span>
                                            <span className="stat-value">
                                                {formatDuration(targetActivity.moving_time)}
                                            </span>
                                        </div>
                                        {targetActivity.average_watts && (
                                            <div className="stat">
                                                <span className="stat-label">Power</span>
                                                <span className="stat-value">
                                                    {targetActivity.average_watts.toFixed(0)} W
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ) : null}
                    </div>

                    {/* Water Analysis Summary - only show to admins and when analysis is available */}
                    {isWaterAnalyzed && waterSummary && isAdmin && (
                        <div style={{
                            backgroundColor: '#e8f4f8',
                            border: '1px solid #b8dce8',
                            borderRadius: '8px',
                            padding: '16px',
                            marginBottom: '20px'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                                <span style={{ fontSize: '16px', fontWeight: '600', color: '#2c5aa0' }}>
                                    🌊 Water Content Analysis
                                </span>
                            </div>
                            <div style={{ fontSize: '14px', color: '#2c5aa0', lineHeight: '1.4' }}>
                                <div style={{ marginBottom: '4px' }}>
                                    <strong>{waterSummary.percentage}% water content</strong> - {waterSummary.description}
                                </div>
                                <div style={{ display: 'flex', gap: '16px', fontSize: '12px' }}>
                                    <span>Water: {waterSummary.percentage}%</span>
                                    <span>Showing: {filteredTemplates.length} styles</span>
                                    <span>Recommended: {recommendedStyles.length} styles</span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 2: Map Styles Section */}
                    {targetActivity && (
                        <div id="styles-section" className="history-page collapsible-section" style={{ marginBottom: '40px' }}>
                            <div
                                className="share-header-row collapsible-header"
                                style={{ marginBottom: '20px', cursor: 'pointer' }}
                                onClick={toggleStylesSection}
                            >
                                <div>
                                    <h2 style={{
                                        fontSize: '30px',
                                        fontWeight: '500',
                                        lineHeight: '1.27',
                                        color: 'var(--Gray-900, #101828)',
                                        margin: '0 0 4px 0',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px'
                                    }}>
                                        2. Select your Mythic Style
                                        <i className={`ti ${isStylesSectionExpanded ? 'ti-chevron-up' : 'ti-chevron-down'}`}
                                            style={{ fontSize: '24px', color: 'var(--Gray-500)' }}></i>
                                    </h2>
                                    <p className="history-subtitle">
                                        {selectedTemplate ?
                                            `Selected: ${selectedTemplate.name}` :
                                            'Choose the perfect style to bring your adventure to life'
                                        }
                                    </p>
                                </div>
                                {selectedTemplate && (
                                    <div className="selection-status">
                                        <i className="ti ti-circle-check" style={{ fontSize: '24px', color: 'var(--Primary-600)' }}></i>
                                    </div>
                                )}
                            </div>
                            <div className="history-divider"></div>

                            {/* Collapsed Style Preview */}
                            {!isStylesSectionExpanded && selectedTemplate && (
                                <div className="collapsed-style-card">
                                    <div className="style-thumbnail">
                                        <img
                                            src={selectedTemplate.exampleImageUrl || '/images/default-style.svg'}
                                            alt={`${selectedTemplate.name} Style Map`}
                                            onError={(e) => {
                                                e.target.src = '/images/default-style.svg';
                                            }}
                                        />
                                    </div>
                                    <div className="style-details">
                                        <div className="style-title">
                                            <h3>{selectedTemplate.name}</h3>
                                        </div>
                                        {selectedTemplate.description && (
                                            <p className="style-description">{selectedTemplate.description}</p>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className={`collapsible-content ${isStylesSectionExpanded ? 'expanded' : 'collapsed'}`}>
                                {isLoadingTemplates ? (
                                    <p style={{ textAlign: 'center' }}>Loading map styles...</p>
                                ) : filteredTemplates.length > 0 ? (
                                    <>
                                        <ul className="history-requests-list">
                                            {currentStyles.map((template, index) => {
                                                const templateId = template.id || `template-${index}`;
                                                return (
                                                    <li
                                                        key={templateId}
                                                        className={`map-style-card ${selectedTemplate?.id === templateId ? 'selected-template' : ''}`}
                                                        onClick={() => handleTemplateSelect({ ...template, id: templateId })}
                                                    >
                                                        <div className="history-thumbnail">
                                                            <img
                                                                src={template.exampleImageUrl || '/images/default-style.svg'}
                                                                alt={`${template.name} Style Map`}
                                                                onError={(e) => {
                                                                    e.target.src = '/images/default-style.svg';
                                                                }}
                                                            />
                                                        </div>
                                                        <div className="history-details" style={{ padding: '20px' }}>
                                                            <h3 className="activity-name" style={{
                                                                margin: '0 0 8px 0',
                                                                fontSize: '18px',
                                                                fontWeight: '600'
                                                            }}>{template.name}</h3>
                                                            {template.description && (
                                                                <p style={{
                                                                    margin: '0',
                                                                    fontSize: '14px',
                                                                    color: 'var(--Gray-500, #667085)',
                                                                    lineHeight: '1.5'
                                                                }}>{template.description}</p>
                                                            )}
                                                        </div>
                                                        {selectedTemplate?.id === templateId && (
                                                            <div className="selection-indicator">
                                                                <i className="ti ti-circle-check" style={{ fontSize: '24px', color: 'var(--Primary-600)' }}></i>
                                                            </div>
                                                        )}
                                                    </li>
                                                );
                                            })}
                                        </ul>

                                        {totalStylePages > 1 && (
                                            <div className="pagination-controls">
                                                <button onClick={() => setCurrentStylePage(prev => prev - 1)} disabled={currentStylePage === 1}>
                                                    &larr; Previous
                                                </button>
                                                <span>Page {currentStylePage} of {totalStylePages}</span>
                                                <button onClick={() => setCurrentStylePage(prev => prev + 1)} disabled={currentStylePage === totalStylePages}>
                                                    Next &rarr;
                                                </button>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <p style={{ textAlign: 'center' }}>No map styles available.</p>
                                        {promptTemplates.length > 0 && filteredTemplates.length === 0 && (
                                            <p style={{ color: '#666', fontSize: '14px', fontStyle: 'italic', textAlign: 'center', marginTop: '10px' }}>
                                                All styles have been filtered based on this route's water content and coastal/land restrictions.
                                            </p>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Step 3: Build the Myth Section */}
                    {targetActivity && (
                        <div id="build-myth-section" className="history-page" style={{ marginBottom: '40px' }}>
                            <h2 style={{
                                fontSize: '30px',
                                fontWeight: '500',
                                lineHeight: '1.27',
                                color: 'var(--Gray-900, #101828)',
                                margin: '0 0 4px 0'
                            }}>3. Build the Myth</h2>
                            <p className="history-subtitle">Let's generate the most epic map for your activity</p>
                            <div className="history-divider"></div>

                            <div className="generation-section">
                                {/* Selected Activity Summary */}
                                <div className="selected-summary">
                                    <h3>Selected Activity</h3>
                                    <div className="summary-card">
                                        <ActivityIcon type={targetActivity.type} size={20} />
                                        <span>{targetActivity.name}</span>
                                        <span className="summary-date">
                                            {new Date(targetActivity.start_date).toLocaleDateString()}
                                        </span>
                                    </div>
                                </div>

                                {/* Selected Template Summary */}
                                {selectedTemplate && (
                                    <div className="selected-summary">
                                        <h3>Selected Style</h3>
                                        <div className="summary-card">
                                            <span>{selectedTemplate.name}</span>
                                            {selectedTemplate.description && (
                                                <span className="summary-description">{selectedTemplate.description}</span>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Generation Button */}
                                <div className="generation-controls">
                                    <button
                                        onClick={handleGenerateMap}
                                        disabled={!selectedTemplate || isGenerating}
                                        className="submit-generation-button"
                                    >
                                        {isGenerating ? 'Generating Your Mythic Map...' : 'Make it Mythic'}
                                    </button>

                                    {!selectedTemplate && (
                                        <p className="generation-hint">
                                            Please select a style to generate your map
                                        </p>
                                    )}
                                </div>

                                {/* Status Messages */}
                                {statusError && (
                                    <p className="error" style={{ marginTop: '20px', textAlign: 'center' }}>
                                        {statusError}
                                    </p>
                                )}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

// ActivityIcon component
const ActivityIcon = ({ type, size = 24 }) => {
    let iconClass = 'ti ti-activity';

    switch (type?.toLowerCase()) {
        case 'run':
            iconClass = 'ti ti-run';
            break;
        case 'ride':
        case 'virtualride':
        case 'ebikeride':
            iconClass = 'ti ti-bike';
            break;
        case 'walk':
            iconClass = 'ti ti-walk';
            break;
        case 'swim':
            iconClass = 'ti ti-swimming';
            break;
        case 'hike':
            iconClass = 'ti ti-trekking';
            break;
        case 'workout':
            iconClass = 'ti ti-stretching';
            break;
        default:
            iconClass = 'ti ti-activity';
    }

    return <i className={iconClass} style={{ fontSize: `${size}px` }}></i>;
};

export default TargettedActivityGeneration;